import * as React from 'react';
import styled, { css } from 'styled-components';

import { Icon } from 'components/common';
import { Button } from 'components/bootstrap';
import type { InvestigationsDetailsAPIType } from 'security-app/hooks/api/investigationsAPI.types';

import Notes from './Notes';
import { Assets, Dashboards, Events, Logs, Searches } from './Evidence';

const InvestigationsSmallRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0.4rem;
`;

const SmallItem = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const LargeItem = styled.div`
  display: flex;
  flex-direction: row;
  flex: 3;
`;

const InvestigationDetailsContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.4rem;
`;

const InvestigationDetailsSmallColumn = styled.div<{ $showAssetsColumn: boolean }>(
  ({ $showAssetsColumn }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    transition: all 0.33s ease-in-out;
    display: ${$showAssetsColumn ? 'inline' : 'none'};

    > :not(:last-child) {
      margin-bottom: 0.4rem;
    }
  `,
);

const InvestigationDetailsLargeColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 3;

  > :not(:last-child) {
    margin-bottom: 0.4rem;
  }
`;

type Props = {
  investigation: InvestigationsDetailsAPIType;
};

const InvestigationDetails = ({ investigation }: Props) => {
  const [showAssetsColumn, setShowAssetsColumn] = React.useState<boolean>(true);
  const [expandedSections, setExpandedSections] = React.useState<string[]>(['events']);

  const toggleExpandedSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter((sectionItem) => sectionItem !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  return (
    <div>
      <InvestigationsSmallRow>
        <SmallItem>
          <h2 style={{ marginRight: '1rem', display: 'flex', alignItems: 'center' }}>Assets Column</h2>
          <Button data-testid="toggle-assets-column" onClick={() => setShowAssetsColumn((cur) => !cur)}>
            {showAssetsColumn ? (
              <Icon style={{ marginRight: '0.4rem' }} name="visibility_off" size="sm" />
            ) : (
              <Icon style={{ marginRight: '0.4rem' }} name="visibility" size="sm" />
            )}
            {showAssetsColumn ? 'Hide' : 'Show'}
          </Button>
        </SmallItem>
        <LargeItem>
          <Dashboards investigation={investigation} />
        </LargeItem>
      </InvestigationsSmallRow>
      <InvestigationDetailsContentContainer>
        <InvestigationDetailsSmallColumn $showAssetsColumn={showAssetsColumn}>
          <Assets investigation={investigation} />
          <Notes investigation={investigation} />
        </InvestigationDetailsSmallColumn>
        <InvestigationDetailsLargeColumn>
          <Events
            investigation={investigation}
            isExpanded={expandedSections.includes('events')}
            onToggleExpanded={toggleExpandedSection}
          />
          <Logs
            investigation={investigation}
            isExpanded={expandedSections.includes('logs')}
            onToggleExpanded={toggleExpandedSection}
          />
          <Searches
            investigation={investigation}
            isExpanded={expandedSections.includes('searches')}
            onToggleExpanded={toggleExpandedSection}
          />
        </InvestigationDetailsLargeColumn>
      </InvestigationDetailsContentContainer>
    </div>
  );
};

export default InvestigationDetails;
